/* eslint-disable @typescript-eslint/naming-convention */
import axios from 'axios';
import { action, computed, makeObservable, observable, runInAction } from 'mobx';

import { toast } from '@servicetitan/anvil2';
import { inject, injectable } from '@servicetitan/react-ioc';

import {
    InstalledEquipmentApiModel,
    MappedEquipmentApiModel,
} from '../../common/api/equipments.api';
import {
    PartFindApi,
    PartResponseModel,
    PartWithId,
    PricebookMatchItem,
} from '../../common/api/part-find.api';
import { MessageType } from '../../common/models/message-type.model';
import { ChatStore } from '../../common/stores/chat.store';
import { OemPart } from '../models/oem-part';
import {
    ReplacementPartViewModel,
    ReplacementPartViewModelWithMatch,
} from '../models/replacement-part-view-model';

@injectable()
export class ReplacementPartFinderStore {
    @observable isLoading = false;
    @observable isLoadingPricebookMatches = false;

    @observable selectedOemPart?: OemPart;

    @observable partsResponse?: PartResponseModel = undefined;
    @observable pricebookMatchItems: PricebookMatchItem[] = [];

    @observable selectedPart?: ReplacementPartViewModelWithMatch = undefined;

    constructor(
        @inject(ChatStore) private readonly chatStore: ChatStore,
        @inject(PartFindApi) private readonly partFindApi: PartFindApi
    ) {
        makeObservable(this);
        this.chatStore.resetMessaging();
        chatStore.enqueueMessages([
            {
                isMine: false,
                showAvatar: true,
                type: MessageType.Text,
                text: 'Choose one of the two options below to select equipment.',
            },
        ]);
    }

    @action
    dispose = () => {
        this.selectedOemPart = undefined;
    };

    @action
    setSelectedReplacementPart = (part: ReplacementPartViewModelWithMatch) => {
        this.selectedPart = part;
    };

    @action
    showSelectEquipment = () => {
        this.chatStore.enqueueMessages([
            {
                isMine: false,
                type: MessageType.SearchEquipment,
            },
            {
                type: MessageType.ManualEquipment,
                isMine: false,
                date: new Date(),
                showBubble: true,
            },
        ]);
    };

    showPartSelection = () => {
        if (this.allParts.length === 0) {
            this.chatStore.enqueueMessages([
                {
                    isMine: false,
                    showAvatar: true,
                    date: new Date(),
                    type: MessageType.Text,
                    text: 'Sorry, there are no parts available for the selected Model Number',
                },
            ]);
        } else {
            this.chatStore.enqueueMessages([
                {
                    isMine: false,
                    showAvatar: true,
                    type: MessageType.ReplacementOEMPartSelect,
                    text: 'What OEM part do you want to find a replacement for?',
                },
            ]);
        }
    };

    @action
    setSelectedOemPart = async (oemPart: OemPart) => {
        this.selectedOemPart = oemPart;

        this.chatStore.enqueueMessages([
            {
                isMine: true,
                showAvatar: false,
                date: new Date(),
                type: MessageType.Text,
                text: oemPart.label,
            },
        ]);

        if (this.replacementParts.length === 0) {
            this.chatStore.enqueueMessages([
                {
                    isMine: false,
                    showAvatar: true,
                    type: MessageType.Text,
                    text: `Sorry, there are no parts available for the selected Model Number`,
                },
            ]);
        } else {
            this.chatStore.enqueueMessages([
                {
                    isMine: false,
                    showAvatar: true,
                    type: MessageType.Text,
                    text: `Here are compatible replacements`,
                },
            ]);
            await this.fetchPriceBookMatches();
            this.chatStore.enqueueMessages([
                {
                    id: `replacement-part-${oemPart.id}`,
                    scrollToId: true,
                    isMine: false,
                    showAvatar: false,
                    date: new Date(),
                    type: MessageType.ReplacementsList,
                    text: '',
                    data: [...this.replacementParts],
                },
            ]);
        }
    };

    /**
     * this is the list od selected Equipment/model parts
     */
    @computed
    get allParts(): PartWithId[] {
        if (!this.partsResponse) {
            return [];
        }
        return Object.entries(this.partsResponse.part).map<PartWithId>(([key, value]) => ({
            id: key,
            ...value,
        }));
    }

    @computed
    get allPartsViewModels() {
        return this.allParts.map<ReplacementPartViewModel>(value => ({
            id: value.id,
            label: `${value.brand} - ${value.number}`,
            brand: value.brand,
            partNumber: value.number,
            subcategory: value.subcategory,
            imageUrl: value.image_url,
            score: value.score ?? 0,
            matchQuality:
                value.score === 1
                    ? 'Low'
                    : value.score === 2
                      ? 'Medium'
                      : value.score === 3
                        ? 'High'
                        : 'High',
        }));
    }

    /**
     * this is the list of OEM parts showing in dropdown
     */
    @computed
    get oemParts() {
        if (!this.partsResponse) {
            return [];
        }
        const ids = Object.keys(this.partsResponse.replacement);
        return ids
            .map(id => {
                const part = this.allPartsViewModels.find(p => p.id === id);
                if (!part) {
                    return null;
                }
                const replacementPartIds = this.partsResponse!.replacement[id] ?? [];
                return {
                    id,
                    disabled: replacementPartIds.length === 0,
                    label: part.subcategory,
                    partNumber: part.partNumber,
                    disabledReason:
                        replacementPartIds.length === 0
                            ? 'no replacement parts available'
                            : undefined,
                };
            })
            .filter(x => !!x);
    }

    /**
     * this is the list of replacement part list to show in chat
     * OEM part should be selected
     */
    @computed
    get replacementParts(): ReplacementPartViewModelWithMatch[] {
        if (!this.selectedOemPart || !this.partsResponse) {
            return [];
        }
        const replacementPartIds = this.partsResponse.replacement[this.selectedOemPart.id] ?? [];
        const replacementsParts = this.allPartsViewModels.filter(p =>
            replacementPartIds.includes(p.id)
        );

        const replacementPartsWithPricebookMatches: ReplacementPartViewModelWithMatch[] = [];
        replacementsParts.forEach(part => {
            // bluon-id
            const partId = part.id;

            const matches = this.pricebookMatchItems.filter(x => x.bluon_id === partId);
            if (matches.length === 0) {
                replacementPartsWithPricebookMatches.push(part);
            } else {
                matches.forEach(match => {
                    const partWithMatch = { ...part, match };

                    replacementPartsWithPricebookMatches.push(partWithMatch);
                });
            }
        });

        replacementPartsWithPricebookMatches.sort((a, b) => {
            const matchForA = !a.match ? 0 : 4 - a.match.type;
            const matchForB = !b.match ? 0 : 4 - b.match.type;

            const qualityForA = a.match?.quality ?? a.score ?? 0;
            const qualityForB = b.match?.quality ?? b.score ?? 0;

            return matchForA === matchForB ? qualityForB - qualityForA : matchForB - matchForA;
        });

        return replacementPartsWithPricebookMatches;
    }

    @action
    loadParts = async (
        mappedEquipments: MappedEquipmentApiModel[],
        installedEquipment?: InstalledEquipmentApiModel
    ) => {
        this.isLoading = true;
        this.partsResponse = undefined;
        this.selectedOemPart = undefined;

        try {
            const response = await this.partFindApi.findParts(mappedEquipments, installedEquipment);
            runInAction(() => {
                this.partsResponse = response.data;
                this.isLoading = false;
            });
            this.showPartSelection();
        } catch (error) {
            if (axios.isCancel(error)) {
                // skip
                return false;
            }
            if (axios.isAxiosError(error)) {
                // TODO: Handle Axios error
                runInAction(() => {
                    this.isLoading = false;
                });

                this.chatStore.enqueueMessages([
                    {
                        isMine: false,
                        showAvatar: true,
                        date: new Date(),
                        type: MessageType.Text,
                        text: 'Sorry, I was unable to locate any matches, please try modifying your search',
                    },
                ]);
            }

            throw error;
        }
    };

    @action
    fetchPriceBookMatches = async () => {
        this.pricebookMatchItems = [];

        // construct the request data
        const selectedReplacementPartIds = this.replacementParts.map(x => x.id);
        const allPartsWithIds = this.allParts.filter(x =>
            selectedReplacementPartIds.includes(x.id)
        );
        if (allPartsWithIds.length === 0) {
            return;
        }

        const requestData = allPartsWithIds.map(({ image_url, score, ...rest }) => ({ ...rest }));

        this.isLoadingPricebookMatches = true;
        try {
            const res = await this.partFindApi.pricebookMatch(requestData);
            runInAction(() => {
                this.isLoadingPricebookMatches = false;
                this.pricebookMatchItems = res.data?.matched_parts ?? [];
            });
        } catch (error) {
            if (axios.isCancel(error)) {
                return false;
            }
            if (axios.isAxiosError(error)) {
                toast.danger({ title: 'Something went wrong!', duration: 5000 });
                return false;
            }

            runInAction(() => {
                this.isLoadingPricebookMatches = false;
            });
            throw error;
        }
    };
}
